import { useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Navbar } from "../Navbar";
import { Footer } from "../Footer";
import { DocsSidebar } from "./DocsSidebar";
import { DocsSearch } from "./DocsSearch";
import { Menu, X } from "lucide-react";
import { ReadThemeProvider, useReadThemeCtx } from "../../lib/ReadThemeContext";

function DocsLayoutInner() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useReadThemeCtx();
  const isLight = theme === "light";

  // Reset scroll to the top on every docs route change, BEFORE the next paint.
  // useLayoutEffect runs synchronously after DOM mutations, so the user never
  // sees the previous page's scroll position bleed through.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Intercept clicks on internal /docs/ links inside rendered HTML so they route via React Router
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href) return;
      if (a.target === "_blank") return;
      if (href.startsWith("/")) {
        e.preventDefault();
        navigate(href);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  const surface = isLight ? "bg-bone read-light" : "bg-aubergine";
  const mobileBar = isLight
    ? "bg-bone border-b border-aubergine/10 text-aubergine/70 hover:text-aubergine"
    : "bg-aubergine border-b border-champ-light/10 text-bone/70 hover:text-bone";

  return (
    <div className={`min-h-screen ${surface}`} data-testid="docs-layout" data-theme={theme}>
      <Navbar />

      {/* Mobile sidebar toggle */}
      <div className={`md:hidden sticky top-[60px] z-30 px-6 py-2 ${mobileBar}`}>
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          data-testid="docs-mobile-nav-toggle"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em]"
        >
          {mobileNavOpen ? <X className="h-3.5 w-3.5" /> : <Menu className="h-3.5 w-3.5" />}
          {mobileNavOpen ? "Close menu" : "Browse docs"}
        </button>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 pt-20 md:pt-24 pb-20">
        <div className="grid md:grid-cols-12 gap-8 md:gap-10 lg:gap-12">
          {/* Sidebar */}
          <div className={`md:col-span-4 lg:col-span-3 ${mobileNavOpen ? "block" : "hidden md:block"}`}>
            <div className="md:sticky md:top-28 md:max-h-[calc(100vh-8rem)] md:overflow-y-auto pr-2">
              <DocsSidebar onOpenSearch={() => setSearchOpen(true)} />
            </div>
          </div>

          {/* Main + TOC */}
          <div className="md:col-span-8 lg:col-span-9">
            <Outlet />
          </div>
        </div>
      </div>

      <Footer />
      <DocsSearch open={searchOpen} onClose={() => setSearchOpen(false)} theme={theme} />
    </div>
  );
}

export function DocsLayout() {
  return (
    <ReadThemeProvider>
      <DocsLayoutInner />
    </ReadThemeProvider>
  );
}
